import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Table, Button, Badge, Modal, Spinner, Alert } from 'react-bootstrap';
import { FaEye, FaCheck, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';

const DashboardContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);

  // API URL - use environment variable or default to localhost
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8081';

  // Fetch contacts from API
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('You must be logged in to view contacts');
        setLoading(false);
        return;
      }
      
      const response = await axios.get(`${API_URL}/api/contact`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      setContacts(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setError(err.response?.data?.error || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  // Update contact status
  const updateContactStatus = async (id, status) => {
    setStatusUpdateLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/contact/${id}`, 
        { status },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Update local state
      setContacts(contacts.map(contact => 
        contact._id === id ? { ...contact, status } : contact
      ));
      
      // If the selected contact is being updated, update it too
      if (selectedContact && selectedContact._id === id) {
        setSelectedContact({ ...selectedContact, status });
      }
      
      setStatusUpdateSuccess(true);
      setTimeout(() => setStatusUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating contact status:', err);
      setError(err.response?.data?.error || 'Failed to update contact status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  // View contact details
  const viewContactDetails = (contact) => {
    setSelectedContact(contact);
    setShowModal(true);
  };

  // Format date
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'new':
        return <Badge bg="primary">New</Badge>;
      case 'contacted':
        return <Badge bg="warning">Contacted</Badge>;
      case 'resolved':
        return <Badge bg="success">Resolved</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Load contacts on component mount
  useEffect(() => {
    fetchContacts();
  }, []);

  return (
    <div className="dashboard-contacts p-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Contact Form Submissions</h2>
        <Button 
          variant="outline-primary" 
          onClick={fetchContacts}
          disabled={loading}
        >
          {loading ? <Spinner animation="border" size="sm" /> : 'Refresh'}
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {statusUpdateSuccess && (
        <Alert variant="success" className="mb-4">
          Contact status updated successfully!
        </Alert>
      )}

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" />
          <p className="mt-2">Loading contacts...</p>
        </div>
      ) : contacts.length === 0 ? (
        <Alert variant="info">
          No contact form submissions found.
        </Alert>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Course</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact) => (
                <tr key={contact._id}>
                  <td>{contact.name}</td>
                  <td>
                    <a href={`mailto:${contact.email}`} className="text-decoration-none">
                      {contact.email}
                    </a>
                  </td>
                  <td>
                    <a href={`tel:${contact.mobile}`} className="text-decoration-none">
                      {contact.mobile}
                    </a>
                  </td>
                  <td>{contact.course}</td>
                  <td>{formatDate(contact.createdAt)}</td>
                  <td>{getStatusBadge(contact.status)}</td>
                  <td>
                    <Button 
                      variant="outline-info" 
                      size="sm" 
                      className="me-2"
                      onClick={() => viewContactDetails(contact)}
                    >
                      <FaEye /> View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      {/* Contact Details Modal */}
      <Modal 
        show={showModal} 
        onHide={() => setShowModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Contact Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedContact && (
            <>
              <div className="d-flex justify-content-between mb-4">
                <h4>{selectedContact.name}</h4>
                <div>{getStatusBadge(selectedContact.status)}</div>
              </div>
              
              <div className="row mb-4">
                <div className="col-md-6">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${selectedContact.email}`} className="text-decoration-none">
                      <FaEnvelope className="me-1" />
                      {selectedContact.email}
                    </a>
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Mobile:</strong>{' '}
                    <a href={`tel:${selectedContact.mobile}`} className="text-decoration-none">
                      <FaPhoneAlt className="me-1" />
                      {selectedContact.mobile}
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="mb-4">
                <p><strong>Course of Interest:</strong> {selectedContact.course}</p>
                <p><strong>Submitted on:</strong> {formatDate(selectedContact.createdAt)}</p>
              </div>
              
              <div className="mb-4">
                <h5>Message:</h5>
                <div className="p-3 bg-light rounded">
                  {selectedContact.message.split('\n').map((line, i) => (
                    <p key={i} className="mb-1">{line}</p>
                  ))}
                </div>
              </div>
              
              <div className="mt-4">
                <h5>Update Status:</h5>
                <div className="d-flex gap-2 mt-3">
                  <Button 
                    variant={selectedContact.status === 'new' ? 'primary' : 'outline-primary'}
                    onClick={() => updateContactStatus(selectedContact._id, 'new')}
                    disabled={statusUpdateLoading || selectedContact.status === 'new'}
                  >
                    New
                  </Button>
                  <Button 
                    variant={selectedContact.status === 'contacted' ? 'warning' : 'outline-warning'}
                    onClick={() => updateContactStatus(selectedContact._id, 'contacted')}
                    disabled={statusUpdateLoading || selectedContact.status === 'contacted'}
                  >
                    Contacted
                  </Button>
                  <Button 
                    variant={selectedContact.status === 'resolved' ? 'success' : 'outline-success'}
                    onClick={() => updateContactStatus(selectedContact._id, 'resolved')}
                    disabled={statusUpdateLoading || selectedContact.status === 'resolved'}
                  >
                    Resolved
                  </Button>
                </div>
                {statusUpdateLoading && (
                  <div className="mt-2">
                    <Spinner animation="border" size="sm" /> Updating status...
                  </div>
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardContacts;
